<script setup lang="ts">
import { onUnmounted } from 'vue';

import {
  CoreEventSource,
  TemplateIds,
} from '@apps/main/new-desktop/core/models';
import { usePerformanceStore } from '@apps/main/new-desktop/core/stores';
import {
  FerryListPageComponentAsyncImport,
  FlightListPageComponentAsyncImport,
  HotelSelectionPageComponentAsyncImport,
  ActivitySelectionPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
  ActivityDetailsPageComponentAsyncImport,
  BuildYourTripPageComponentAsyncImport,
} from '@apps/main/modules/router/tripPlannerRoute';

import { isFeatureToggleActive } from '@libs/feature-toggle';
import { useBackgroundComponentsPreload } from '@libs/utils';

import { useTripOverviewPageStore } from '../../stores';
import TripOverviewHeader from '../tripOverviewHeader/TripOverviewHeader.vue';
import FirstDayTripPriceRibbon from '../../../../core/components/FirstDayTripPriceRibbon.vue';

import TripItinerary from './TripItinerary.vue';
import TripItineraryLoader from './loaders/TripItineraryLoader.vue';

const tripOverviewPageStore = useTripOverviewPageStore();
const performanceStore = usePerformanceStore();

const preloadComponents = useBackgroundComponentsPreload([
  HotelSelectionPageComponentAsyncImport,
  HotelDetailsPageComponentAsyncImport,
  FlightListPageComponentAsyncImport,
  ActivitySelectionPageComponentAsyncImport,
  BuildYourTripPageComponentAsyncImport,
  FerryListPageComponentAsyncImport,
  ActivityDetailsPageComponentAsyncImport,
]);

function onContentRendered(): void {
  performanceStore.trackTripOverviewPageRenderedWithContentPerformance();

  preloadComponents.start();
}

onUnmounted(() => preloadComponents.stop());
</script>

<template>
  <div class="trip-overview-column">
    <div class="trip-overview-column-header">
      <TripOverviewHeader />
      <div class="price-ribbon-wrapper">
        <FirstDayTripPriceRibbon
          :id="TemplateIds.PriceCalendar"
          :show-month-select-dropdown="false"
          :source="CoreEventSource.TripOverview"
          scroll-track-theme="white"
        />
      </div>
    </div>

    <TripItinerary
      v-if="
        tripOverviewPageStore.anyItineraryLoaded &&
        !isFeatureToggleActive('loader')
      "
      :print-mode="false"
      @vue:mounted="onContentRendered()"
    />
    <TripItineraryLoader v-else />
  </div>
</template>

<style scoped lang="scss">
.trip-overview-column {
  border-radius: var(--border-radius-12);
  background-color: var(--color-off-white);
  .trip-overview-column-header {
    background-color: var(--color-white);
    .price-ribbon-wrapper {
      padding: 0 var(--spacing-24);
      background-color: var(--color-white);

      @include for-less-or-equal-than-599-px {
        padding: 0;
      }
    }

    .global-suggestion {
      width: 100%;
      display: flex;
      justify-content: center;
    }
  }

  @include for-less-or-equal-than-899-px {
    border-radius: 0;
  }
}
</style>
